import { useEffect } from 'react';
import { create } from 'zustand';
import type { Ticker24h } from 'hopcharts';
import { marketProvider, queryClient } from './market';
import { symbolKey } from './symbols';
import type { SymbolRef } from './symbols';

interface TickerState {
  byKey: Record<string, Ticker24h>;
  put: (tickers: Ticker24h[]) => void;
}

/** Latest 24h ticker per market ("exchange:BASE/QUOTE"), fed by snapshots and live streams. */
export const useTickerStore = create<TickerState>((set) => ({
  byKey: {},
  put: (tickers) =>
    set((state) => {
      if (tickers.length === 0) return state;
      const next = { ...state.byKey };
      for (const t of tickers) next[`${t.exchangeId}:${t.pair}`] = t;
      return { byKey: next };
    }),
}));

/** All 24h tickers of one exchange (shared, refreshed at most every 30 s). */
export function fetchExchangeTickers(exchangeId: string): Promise<Ticker24h[]> {
  return queryClient.fetchQuery({
    queryKey: ['tickers', exchangeId],
    queryFn: () => (marketProvider.getTickers ? marketProvider.getTickers(exchangeId) : Promise.resolve([])),
    staleTime: 30_000,
  });
}

// A few markets' snapshot asks for just those (Bybit's full list is ~430 KB, sent uncompressed), unless
// the exchange's full list is already cached.
const FEW_TICKERS = 10;

function snapshotFor(exchangeId: string, pairs: string[]): Promise<Ticker24h[]> {
  const fresh = Date.now() - (queryClient.getQueryState(['tickers', exchangeId])?.dataUpdatedAt ?? 0) < 30_000;
  if (pairs.length > FEW_TICKERS || fresh || !marketProvider.getTickers) return fetchExchangeTickers(exchangeId);
  return marketProvider.getTickers(exchangeId, pairs).then((tickers) => {
    // An exchange that cannot fetch a few markets on their own answers with its full list: keep that for the
    // next snapshot instead of downloading it again.
    if (tickers.length > pairs.length) queryClient.setQueryData(['tickers', exchangeId], tickers);
    return tickers;
  });
}

// Reference-counted interest per market; one stream per exchange covers the union.
const interest = new Map<string, number>();
const streams = new Map<string, { keys: string; stop: () => void }>();
let syncTimer: ReturnType<typeof setTimeout> | null = null;

function scheduleSync() {
  if (syncTimer) clearTimeout(syncTimer);
  syncTimer = setTimeout(syncStreams, 250);
}

function syncStreams() {
  syncTimer = null;
  const byExchange = new Map<string, string[]>();
  for (const [key, count] of interest) {
    if (count <= 0) continue;
    const i = key.indexOf(':');
    const exchangeId = key.slice(0, i);
    const list = byExchange.get(exchangeId) ?? [];
    list.push(key.slice(i + 1));
    byExchange.set(exchangeId, list);
  }
  for (const [exchangeId, stream] of streams) {
    if (!byExchange.has(exchangeId)) {
      stream.stop();
      streams.delete(exchangeId);
    }
  }
  const put = useTickerStore.getState().put;
  for (const [exchangeId, pairs] of byExchange) {
    const keys = [...pairs].sort().join(',');
    const existing = streams.get(exchangeId);
    if (existing && existing.keys === keys) continue;
    existing?.stop();
    // Snapshot first so rows fill immediately, then live updates.
    snapshotFor(exchangeId, pairs)
      .then((all) => {
        const wanted = new Set(pairs);
        put(all.filter((t) => wanted.has(t.pair)));
      })
      .catch(() => {
        // The stream below still fills the rows when the snapshot fails.
      });
    let stop = () => {};
    if (marketProvider.subscribeTickers) {
      let pending: Ticker24h[] = [];
      let flush: ReturnType<typeof setTimeout> | null = null;
      stop = marketProvider.subscribeTickers(exchangeId, pairs, (ticker) => {
        pending.push(ticker);
        if (!flush) {
          flush = setTimeout(() => {
            flush = null;
            const batch = pending;
            pending = [];
            put(batch);
          }, 250);
        }
      });
    }
    streams.set(exchangeId, { keys, stop });
  }
}

/** Keep live 24h tickers for these markets in the ticker store while mounted. */
export function useLiveTickers(refs: SymbolRef[]): void {
  const keys = refs.map(symbolKey).sort().join('|');
  useEffect(() => {
    const list = keys ? keys.split('|') : [];
    for (const key of list) interest.set(key, (interest.get(key) ?? 0) + 1);
    scheduleSync();
    return () => {
      for (const key of list) {
        const next = (interest.get(key) ?? 1) - 1;
        if (next <= 0) interest.delete(key);
        else interest.set(key, next);
      }
      scheduleSync();
    };
  }, [keys]);
}

export function useTicker(ref: SymbolRef | null): Ticker24h | undefined {
  return useTickerStore((s) => (ref ? s.byKey[symbolKey(ref)] : undefined));
}
